"use client"

import { useEffect, useState } from "react"
import {
  Container,
  TextField,
  Typography,
  Button,
  Grid,
  Box,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  CircularProgress,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteIcon from "@mui/icons-material/Delete"
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { createSale } from "../../redux/actions/sales"
import { fetchAllShops, fetchShopProducts, selectShop } from "../../redux/actions/shop"
import { toast } from "react-toastify"
import { fetchDebtClients } from "../../redux/actions/sales"
import { useNavigate } from "react-router-dom"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"

const PurchaseForm = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const { user } = useSelector((state) => state.user)
  const { selectedShop, products, shops } = useSelector((state) => state.shop)
  const { debtClients } = useSelector((state) => state.sales)

  const [sale, setSale] = useState({
    shop:
      user?.role === "superadmin" || user?.role === "supervisor"
        ? ""
        : typeof user?.shop === "object"
          ? user.shop._id
          : user.shop,
    client_name: "",
    client_phone: "",
    client_address: "",
    products: [
      {
        product: "",
        category: "",
        price_achat: 0,
        prix_de_vente: 0,
        quantity: 1,
        price: "",
      },
    ],
    total_amount: 0,
    payment_method: "cash",
    amount_paid: 0,
    payment_proof: null,
  })

  const [shopName, setShopName] = useState("")
  const [isNewClient, setIsNewClient] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      dispatch(fetchAllShops())
      if (selectedShop) {
        setSale((prev) => ({ ...prev, shop: selectedShop._id }))
        dispatch(fetchShopProducts(selectedShop._id))
        setShopName(selectedShop.name)
      }
    } else if (user?.shop) {
      const shopId = typeof user.shop === "object" ? user.shop._id : user.shop
      dispatch(fetchShopProducts(shopId))
      setSale((prev) => ({ ...prev, shop: shopId }))
      setShopName(typeof user.shop === "object" ? user.shop.name : "")
    }
  }, [user])

  useEffect(() => {
    if (selectedShop && (user?.role === "superadmin" || user?.role === "supervisor")) {
      setSale((prev) => ({ ...prev, shop: selectedShop._id }))
      dispatch(fetchShopProducts(selectedShop._id))
      setShopName(selectedShop.name)
    }
  }, [selectedShop, user?.role])

  useEffect(() => {
    if (sale.payment_method === "debt" && sale.shop) {
      dispatch(fetchDebtClients(sale.shop))
    }
  }, [sale.payment_method, sale.shop])

  const handleProductChange = (index, productId) => {
    const selectedProduct = products.find((p) => p._id === productId)
    if (selectedProduct) {
      const updatedProducts = [...sale.products]
      updatedProducts[index] = {
        ...updatedProducts[index],
        product: selectedProduct._id,
        name: selectedProduct.name,
        category: selectedProduct.category,
        price_achat: selectedProduct.price_achat,
        prix_de_vente: selectedProduct.prix_de_vente,
        quantity: updatedProducts[index].quantity || 1,
      }

      setSale({ ...sale, products: updatedProducts })
      calculateTotalAmount(updatedProducts)
    }
  }

  const handleQuantityChange = (index, quantity) => {
    const updatedProducts = [...sale.products]
    updatedProducts[index].quantity = Number(quantity) > 0 ? Number(quantity) : 1
    setSale({ ...sale, products: updatedProducts })
    calculateTotalAmount(updatedProducts)
  }

  const handlePriceChange = (index, price) => {
    const updatedProducts = [...sale.products]
    updatedProducts[index].prix_de_vente = Number(price) >= 0 ? Number(price) : 0
    setSale({ ...sale, products: updatedProducts })
    calculateTotalAmount(updatedProducts)
  }

  const calculateTotalAmount = (products) => {
    const total = products.reduce((acc, item) => {
      const quantity = Number(item.quantity) || 0
      const price = Number(item.prix_de_vente) || 0
      const itemTotal = quantity * price
      return acc + itemTotal
    }, 0)
    setSale((prevSale) => ({ ...prevSale, total_amount: total }))
  }

  const addProduct = () => {
    setSale({
      ...sale,
      products: [
        ...sale.products,
        {
          product: "",
          category: "",
          price_achat: 0,
          prix_de_vente: 0,
          quantity: 1,
          price: 0,
        },
      ],
    })
  }

  const removeProduct = (index) => {
    const updatedProducts = sale.products.filter((_, i) => i !== index)
    setSale({ ...sale, products: updatedProducts })
    calculateTotalAmount(updatedProducts)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitting(true)

    try {
      let shopId
      if (user.role === "superadmin") {
        shopId = selectedShop?._id
      } else {
        shopId = typeof user.shop === "object" ? user.shop._id : user.shop
      }

      if (!shopId) {
        toast.error("Veuillez sélectionner un shop")
        return
      }

      if (!sale.client_name.trim()) {
        toast.error("Nom du client est requis")
        return
      }

      if (!sale.client_phone.trim()) {
        toast.error("Téléphone du client est requis")
        return
      }

      if (sale.products.length === 0) {
        toast.error("Veuillez ajouter au moins un produit")
        return
      }

      for (const product of sale.products) {
        if (!product.product) {
          toast.error("Veuillez sélectionner un produit pour tous les éléments")
          return
        }
        if (!product.quantity || product.quantity <= 0) {
          toast.error("La quantité doit être supérieure à 0 pour tous les produits")
          return
        }
      }

      if (sale.amount_paid < 0) {
        toast.error("Le montant payé ne peut pas être négatif")
        return
      }

      if (sale.payment_method === "cash" || sale.payment_method === "bank") {
        if (sale.amount_paid !== sale.total_amount) {
          toast.error("Le montant payé doit être égal au montant total pour les paiements en espèces ou par banque")
          return
        }
      }

      if (sale.amount_paid > sale.total_amount) {
        toast.error("Le montant payé ne peut pas dépasser le montant total")
        return
      }

      let result

      if (sale.payment_method === "bank") {
        const formData = new FormData()

        Object.keys(sale).forEach((key) => {
          if (key === "products") {
            formData.append("products", JSON.stringify(sale[key]))
          } else if (key === "payment_proof") {
          } else {
            formData.append(key, sale[key])
          }
        })

        formData.append("shop_id", shopId)

        if (sale.payment_proof) {
          formData.append("payment_proof", sale.payment_proof)
        }

        result = await dispatch(createSale(formData))
      } else {
        const saleData = {
          ...sale,
          shop_id: shopId,
        }
        result = await dispatch(createSale(saleData))
      }

      if (result && result.success) {
        toast.success("Vente créée avec succès")

        switch (sale.payment_method) {
          case "cash":
            navigate("/transaction/sales")
            break
          case "bank":
            navigate("/transaction/banque")
            break
          case "debt":
            navigate("/transaction/debt")
            break
        }

        setSale({
          shop_id: shopId,
          client_name: "",
          client_phone: "",
          client_address: "",
          products: [
            {
              product: "",
              category: "",
              quantity: 1,
              price_achat: 0,
              prix_de_vente: 0,
              price: 0,
            },
          ],
          total_amount: 0,
          payment_method: "cash",
          amount_paid: 0,
        })
        setIsNewClient(true)
      }
    } catch (error) {
      console.error("Error creating sale:", error)
      toast.error(error?.response?.data?.message || "Erreur lors de la création de la vente")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleShopChange = (event) => {
    const shopId = event.target.value
    const shop = shops.find((s) => s._id === shopId)
    dispatch(selectShop(shop))
    dispatch(fetchShopProducts(shopId))
  }

  const electronicShops = shops.filter((shop) => shop.enum === "electronics" || shop.type === "electronics")

  const handlePaymentMethodChange = (e) => {
    const method = e.target.value
    setSale((prev) => ({
      ...prev,
      payment_method: method,
      amount_paid: method === "debt" ? 0 : prev.amount_paid,
      payment_proof: method === "bank" ? prev.payment_proof : null,
    }))
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.type.match("image.*")) {
        toast.error("Veuillez télécharger un fichier image valide")
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("La taille du fichier ne doit pas dépasser 5 Mo")
        return
      }

      setSale((prev) => ({
        ...prev,
        payment_proof: file,
      }))
    }
  }

  const removePaymentProof = () => {
    setSale((prev) => ({
      ...prev,
      payment_proof: null,
    }))
  }

  return (
    <Container maxWidth="lg">
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          mt: 4,
          border: `1px solid ${colors.primary[200]}`,
          boxShadow: `0 8px 24px rgba(${colors.primary[600]}, 0.12)`,
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            color: "text.primary",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          Vente des produit
        </Typography>
        <Divider
          sx={{
            mb: 3,
            background: colors.primary[600],
          }}
        />
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {user?.role === "superadmin" || user?.role === "supervisor" ? (
                <FormControl fullWidth>
                  <InputLabel>Sélectionner un Shop</InputLabel>
                  <Select
                    value={selectedShop?._id || ""}
                    onChange={handleShopChange}
                    label="Sélectionner un Shop"
                    required
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary[600],
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary[900],
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary[900],
                      },
                    }}
                  >
                    {electronicShops.map((shop) => (
                      <MenuItem key={shop._id} value={shop._id}>
                        {shop.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField label="Nom du Shop*" value={shopName} fullWidth disabled variant="outlined" />
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Mode de paiement*"
                value={sale.payment_method}
                onChange={handlePaymentMethodChange}
                fullWidth
                select
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary[600],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[900],
                    },
                  },
                }}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank">Bank BGF</MenuItem>
                <MenuItem value="debt">Credit</MenuItem>
              </TextField>

              {sale.payment_method === "bank" && (
                <Box mt={2}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="bank-proof-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="bank-proof-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        mb: 1,
                        borderColor: colors.primary[600],
                        color: colors.primary[600],
                        "&:hover": {
                          borderColor: colors.primary[900],
                          backgroundColor: `rgba(${colors.primary[600]}, 0.1)`,
                        },
                      }}
                    >
                      Télécharger le bordereau
                    </Button>
                  </label>
                  {sale.payment_proof && (
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        Fichier: {sale.payment_proof.name}
                      </Typography>
                      <IconButton size="small" onClick={removePaymentProof} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    Téléchargez une image du bordereau de virement (max 5MB)
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Nom du client*"
                value={sale.client_name}
                onChange={(e) => setSale((prev) => ({ ...prev, client_name: e.target.value }))}
                fullWidth
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary[600],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[900],
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Téléphone du client*"
                value={sale.client_phone}
                onChange={(e) => setSale((prev) => ({ ...prev, client_phone: e.target.value }))}
                fullWidth
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary[600],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[900],
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Adresse du client*"
                value={sale.client_address}
                onChange={(e) => setSale((prev) => ({ ...prev, client_address: e.target.value }))}
                fullWidth
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary[600],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[900],
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Typography
            variant="h6"
            sx={{
              mt: 4,
              mb: 2,
              color: "text.primary",
              fontWeight: "bold",
            }}
          >
            Products
          </Typography>

          {sale.products.map((item, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                background: theme.palette.background.paper,
                border: `1px solid ${colors.primary[200]}`,
                "&:hover": {
                  boxShadow: `0 4px 12px rgba(34, 197, 94, 0.15)`,
                },
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    value={products.find((p) => p._id === item.product) || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleProductChange(index, newValue._id)
                      }
                    }}
                    options={products}
                    getOptionLabel={(option) => option.name || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Produit*" required variant="outlined" fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="Catégorie" value={item.category} fullWidth disabled variant="outlined" />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Prix d'achat"
                    value={item.price_achat?.toLocaleString()}
                    fullWidth
                    disabled
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Prix de vente*"
                    type="number"
                    value={item.prix_de_vente || ""}
                    onChange={(e) => handlePriceChange(index, e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    inputProps={{
                      min: 0,
                      step: "0.01",
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Quantité"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(index, Number.parseInt(e.target.value))}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={1}>
                  <Box display="flex" justifyContent="center">
                    <IconButton
                      onClick={() => removeProduct(index)}
                      color="error"
                      disabled={sale.products.length === 1}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                    {index === sale.products.length - 1 && (
                      <IconButton onClick={addProduct} sx={{ color: colors.primary[600] }}>
                        <AddCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Montant payé"
                type="number"
                value={sale.amount_paid}
                onChange={(e) => setSale((prev) => ({ ...prev, amount_paid: Number.parseFloat(e.target.value) }))}
                fullWidth
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[300],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary[600],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[900],
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "right",
                  backgroundColor: theme.palette.mode === "dark" ? colors.primary[800] : colors.grey[600],
                  WebkitBackgroundClip: "unset",
                  WebkitTextFillColor: "unset",
                  backgroundClip: "unset",
                  color: "white",
                  border: `1px solid ${colors.primary[200]}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: "white" }}>
                  Montant total: <strong>{sale.total_amount} Fbu</strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              mt: 4,
              background: isSubmitting ? colors.grey[400] : colors.primary[600],
              color: "white",
              fontWeight: "bold",
              py: 1.5,
              borderRadius: 2,
              boxShadow: `0 4px 12px rgba(34, 197, 94, 0.3)`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: isSubmitting ? "none" : "translateY(-2px)",
                boxShadow: isSubmitting ? `0 4px 12px rgba(34, 197, 94, 0.3)` : `0 8px 20px rgba(34, 197, 94, 0.4)`,
                backgroundColor: colors.primary[700],
              },
              "&:disabled": {
                color: "white",
                opacity: 0.8,
              },
            }}
          >
            {isSubmitting
              ? sale.payment_method === "bank"
                ? "Téléchargement en cours..."
                : "Traitement en cours..."
              : "Vendre"}
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default PurchaseForm
